import type { AdminOpenClaw } from "../../../types/openclaw";
import type { UseAdminClawsResult } from "../../../hooks/useAdminClaws";
import { useInstanceHealth } from "../../../hooks/useInstanceHealth";
import { useInstanceUsage } from "../../../hooks/useInstanceUsage";
import { useInstanceEmbeddingUsage } from "../../../hooks/useInstanceEmbeddingUsage";
import { useInstanceToolCalls } from "../../../hooks/useInstanceToolCalls";
import { useInstanceMetrics } from "../../../hooks/useInstanceMetrics";
import { useModelUsage } from "../../../hooks/useModelUsage";
import type { InstanceRange } from "../../../lib/instanceDetailRange";
import type { InstanceTab } from "../../../lib/instanceDetailTabs";
import { InstancePanelGrid, PanelPair } from "./InstancePanelGrid";
import { InstanceOverview } from "./InstanceOverview";
import { HealthPanel } from "./panels/HealthPanel";
import { ChannelActivityPanel } from "./panels/ChannelActivityPanel";
import { SpendPanel } from "./panels/SpendPanel";
import { EmbeddingSpendPanel } from "./panels/EmbeddingSpendPanel";
import { BillingPanel } from "./panels/BillingPanel";
import { IntegrationsPanel } from "./panels/IntegrationsPanel";
import { ToolCallsPanel } from "./panels/ToolCallsPanel";
import { ToolLogsPanel } from "./panels/ToolLogsPanel";
import { AuditPanel } from "./panels/AuditPanel";
import { MetricPanel } from "./panels/MetricPanel";
import { GatewayRestartsPanel } from "./panels/GatewayRestartsPanel";
import { SnapshotsPanel } from "./panels/SnapshotsPanel";
import { PluginUsagePanel } from "./panels/PluginUsagePanel";
import { LocalModelsPanel } from "./panels/LocalModelsPanel";
import { AdminSettingsPanel } from "./panels/AdminSettingsPanel";
import { BrowserSettingsPanel } from "./panels/BrowserSettingsPanel";

type TabProps = { readonly claw: AdminOpenClaw; readonly range: InstanceRange };

const OverviewTab = ({ claw, range }: TabProps): JSX.Element => {
  const health = useInstanceHealth(claw.tenantId, range);
  const usage = useInstanceUsage(claw.tenantId, range);
  const toolCalls = useInstanceToolCalls(claw.tenantId, range);
  const metrics = useInstanceMetrics(claw.tenantId, range);
  return (
    <>
      <InstanceOverview claw={claw} range={range} timeline={health.timeline} usage={usage.usage} toolCalls={toolCalls.history} metrics={metrics.metrics}
        sourceStatuses={[health.status, toolCalls.status, metrics.status]} />
      <InstancePanelGrid>
        <PanelPair>
          <HealthPanel range={range} status={health.status} timeline={health.timeline} error={health.error} />
          <ChannelActivityPanel range={range} status={health.status} timeline={health.timeline} error={health.error} />
        </PanelPair>
      </InstancePanelGrid>
    </>
  );
};

// Swap sits beside Memory: swap fills because memory is squeezed, and the two
// only read correctly side by side.
const HealthTab = ({ claw, range }: TabProps): JSX.Element => {
  const metrics = useInstanceMetrics(claw.tenantId, range);
  const metric = (title: string, kind: "cpu" | "mem" | "swap" | "disk"): JSX.Element => (
    <MetricPanel title={title} metric={kind} status={metrics.status} metrics={metrics.metrics} error={metrics.error} />
  );
  return (
    <InstancePanelGrid>
      <GatewayRestartsPanel tenantId={claw.tenantId} range={range} paused={claw.stopped} />
      <PanelPair>
        {metric("Memory", "mem")}
        {metric("Swap", "swap")}
      </PanelPair>
      <PanelPair>
        {metric("CPU", "cpu")}
        {metric("Disk", "disk")}
      </PanelPair>
      <SnapshotsPanel tenantId={claw.tenantId} />
    </InstancePanelGrid>
  );
};

const UsageTab = ({ claw, range }: TabProps): JSX.Element => {
  const usage = useInstanceUsage(claw.tenantId, range);
  const embeddingUsage = useInstanceEmbeddingUsage(claw.tenantId, range);
  const toolCalls = useInstanceToolCalls(claw.tenantId, range);
  const modelUsage = useModelUsage(claw.tenantId, range);
  return (
    <InstancePanelGrid>
      <PanelPair>
        <SpendPanel status={usage.status} usage={usage.usage} error={usage.error} aiMode={claw.aiMode} />
        <EmbeddingSpendPanel status={embeddingUsage.status} usage={embeddingUsage.usage} error={embeddingUsage.error} />
      </PanelPair>
      <PanelPair>
        <ToolCallsPanel status={toolCalls.status} history={toolCalls.history} error={toolCalls.error} />
        <LocalModelsPanel range={range} status={modelUsage.status} usage={modelUsage.usage} error={modelUsage.error} />
      </PanelPair>
      <PluginUsagePanel tenantId={claw.tenantId} range={range} />
    </InstancePanelGrid>
  );
};

type InstanceTabPanelProps = TabProps & {
  readonly tab: InstanceTab;
  readonly adminActions: Pick<UseAdminClawsResult, "setUnlimitedEntitlements" | "setAgentAccess" | "setBonusSlots">;
};

// Only the active tab mounts, so a tab's self-fetching panels load on first visit.
export const InstanceTabPanel = ({ tab, claw, range, adminActions }: InstanceTabPanelProps): JSX.Element => {
  switch (tab) {
    case "overview": return <OverviewTab claw={claw} range={range} />;
    case "health": return <HealthTab claw={claw} range={range} />;
    case "usage": return <UsageTab claw={claw} range={range} />;
    case "integrations": return <InstancePanelGrid><IntegrationsPanel tenantId={claw.tenantId} /></InstancePanelGrid>;
    case "billing": return <InstancePanelGrid><BillingPanel tenantId={claw.tenantId} /></InstancePanelGrid>;
    case "activity":
      return (
        <InstancePanelGrid>
          <AuditPanel tenantId={claw.tenantId} range={range} />
          <ToolLogsPanel tenantId={claw.tenantId} range={range} />
        </InstancePanelGrid>
      );
    case "settings":
      return (
        <InstancePanelGrid>
          <AdminSettingsPanel claw={claw} actions={adminActions} />
          <BrowserSettingsPanel tenantId={claw.tenantId} />
        </InstancePanelGrid>
      );
  }
};
